import React, { useMemo } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  GEO_LOCATION_TYPES,
  IGeoLocationsEditParams,
  IGeoLocationsParams,
} from "~/entity/Geo/model/shema";
import { useGetLocationData } from "~/hooks/locationData";
import { Field } from "~/shared/ui/Field";

import s from "./s.less";

interface Props {
  formId: string;
  onSubmitHandler: (data: IGeoLocationsParams) => void;
  instance?: IGeoLocationsEditParams;
}

const CreateAddressSelectionGuideForm = ({
  formId,
  onSubmitHandler,
  instance,
}: Props) => {
  const { t } = useTranslation();
  const { openModal } = useGetLocationData();

  const typeOptions = useMemo(
    () =>
      GEO_LOCATION_TYPES.map((type) => ({
        value: type,
        label: t(`addressSelectionGuide.typeOptions.${type}`),
      })),
    [t],
  );

  const activeOptions = useMemo(
    () => [
      { value: "true", label: t("addressSelectionGuide.activeYes") },
      { value: "false", label: t("addressSelectionGuide.activeNo") },
    ],
    [t],
  );

  const defaultValues = useMemo(() => {
    if (!instance) return {};

    return {
      name: instance.name,
      nameEn: instance.nameEn,
      index: instance.index,
      cladrCode: instance.cladrCode,
      parentId: instance.parentId,
      okatoCode: instance.okatoCode,
      socr: instance.socr,
      id: instance.id,
      fiasId: instance.fiasId,
      fiasValue: instance.fiasValue,

      type: typeOptions.find((o) => o.value === instance.type),

      active: activeOptions.find((o) => o.value === String(instance.active)),
    };
  }, [instance, typeOptions, activeOptions]);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(
      yup.object().shape({
        name: yup.string().required(t("validation.required")),
        id:
          openModal === "Create"
            ? yup.string()
            : yup.string().required(t("validation.required")),
        nameEn: yup.string().required(t("validation.required")),
        type: yup.object().required(t("validation.required")),
        parentId: yup.mixed().when(["type"], (type, schema) => {
          if (type?.value === "country") {
            return schema.nullable();
          }

          return yup.number().required(t("validation.required"));
        }),
      }),
    ),
  });
  const selectedType = watch("type");
  const isCountry = selectedType?.value === "country";

  const onSubmit = (data) => {
    const preparedData = {
      ...data,
      type: data.type?.value,
      // В селекте значения строковые ("true"/"false") — обратно в boolean для API
      active: data.active ? data.active.value === "true" : undefined,
      parentId: isCountry ? undefined : data.parentId,
    };

    const cleanedData: Partial<IGeoLocationsParams> = {};

    for (const key in preparedData) {
      const typedKey = key as keyof IGeoLocationsParams;
      const value = preparedData[typedKey];

      if (value !== undefined && value !== null) {
        (cleanedData as Record<keyof IGeoLocationsParams, any>)[typedKey] =
          value;
      }
    }

    onSubmitHandler(cleanedData as IGeoLocationsParams);
  };

  return (
    <form id={formId} className={s.wrapper} onSubmit={handleSubmit(onSubmit)}>
      <div className={s.lineText}>
        <span>*</span> – {t("addressSelectionGuide.requiredFields")}
      </div>

      <div className={s.wrapTwo}>
        <Field
          type="input"
          label={t("addressSelectionGuide.id")}
          name="id"
          control={control}
          error={errors.id?.message}
        />

        <Field
          type="input"
          label={t("addressSelectionGuide.parentId")}
          name="parentId"
          control={control}
          error={errors.parentId?.message}
        />
      </div>

      <div className={s.wrapTwo}>
        <Field
          type="input"
          label={t("addressSelectionGuide.name")}
          name="name"
          control={control}
          error={errors.name?.message}
        />
        <Field
          type="input"
          label={t("addressSelectionGuide.nameEn")}
          name="nameEn"
          control={control}
          error={errors.nameEn?.message}
        />
      </div>

      <div className={s.wrapTwo}>
        <Field
          type="select"
          label={t("addressSelectionGuide.type")}
          name="type"
          options={typeOptions}
          labelInValue
          control={control}
          error={(errors["type"] as any)?.message}
        />
        <Field
          type="input"
          label={t("addressSelectionGuide.socr")}
          name="socr"
          control={control}
          error={errors.socr?.message}
        />
        <Field
          type="input"
          label={t("addressSelectionGuide.index")}
          name="index"
          control={control}
          error={errors.index?.message}
        />
      </div>

      <div className={s.wrapTwo}>
        <Field
          type="input"
          label={t("addressSelectionGuide.cladrCode")}
          name="cladrCode"
          control={control}
          error={errors.cladrCode?.message}
        />
        <Field
          type="input"
          label={t("addressSelectionGuide.okatoCode")}
          name="okatoCode"
          control={control}
          error={errors.okatoCode?.message}
        />
      </div>

      <div className={s.wrapTwo}>
        <Field
          type="input"
          label={t("addressSelectionGuide.fiasId")}
          name="fiasId"
          control={control}
          error={errors.fiasId?.message}
        />
        <Field
          type="input"
          label={t("addressSelectionGuide.fiasValue")}
          name="fiasValue"
          control={control}
          error={errors.fiasValue?.message}
        />

        <Field
          type="select"
          label={t("addressSelectionGuide.active")}
          name="active"
          options={activeOptions}
          labelInValue
          control={control}
          error={(errors["active"] as any)?.message}
        />
      </div>
    </form>
  );
};

export default CreateAddressSelectionGuideForm;
